import {FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    CulturesEnum,
    IndividualLocalizedDetailsService,
    IndividualLocalizedDetailInterface,
    IndividualExtendedModel,
    IndividualLocalizedDetailInputModel,
    IndividualLocalizedDetailModel
} from '@spa/api';

import {BaseControl, Helper} from '../../../../shared/classes';

import {
    LocalizedDetailAddressSelector,
    LocalizedDetailCitySelector,
    LocalizedDetailFirstNameSelector,
    LocalizedDetailLastNameSelector,
    LocalizedDetailMiddleNameSelector,
    LocalizedDetailsControls,
    LocalizedDetailStateSelector
} from '../../../../../assets/descriptors';

export class LocalizedDetailsForm {
    private readonly individual: IndividualExtendedModel;
    private readonly localizedDetailsService: IndividualLocalizedDetailsService;

    public formGroup: FormGroup;
    public controls: BaseControl[];

    constructor(
        individual: IndividualExtendedModel,
        localizedDetailsService: IndividualLocalizedDetailsService
    ) {
        this.individual = individual;
        this.localizedDetailsService = localizedDetailsService;
    }

    private localizedDetailsControlsMap(model?: IndividualLocalizedDetailModel): BaseControl[] {
        return LocalizedDetailsControls().map((control: BaseControl) => {
            if (model) {
                switch (control.name) {
                    case LocalizedDetailFirstNameSelector:
                        control.value = model.localizedFirstName;
                        break;
                    case LocalizedDetailMiddleNameSelector:
                        control.value = model.localizedMiddleName;
                        break;
                    case LocalizedDetailLastNameSelector:
                        control.value = model.localizedLastName;
                        break;
                    case LocalizedDetailStateSelector:
                        control.value = model.localizedState;
                        break;
                    case LocalizedDetailCitySelector:
                        control.value = model.localizedCity;
                        break;
                    case LocalizedDetailAddressSelector:
                        control.value = model.localizedAddress;
                        break;
                }
            }

            return control;
        });
    }

    private updateLocalizedDetailMap(
        clonedModel: IndividualLocalizedDetailInterface,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const model = new IndividualLocalizedDetailModel(clonedModel);

            this.individual.localizedDetail.localizedFirstName = model.localizedFirstName;
            this.individual.localizedDetail.localizedMiddleName = model.localizedMiddleName;
            this.individual.localizedDetail.localizedLastName = model.localizedLastName;
            this.individual.localizedDetail.localizedState = model.localizedState;
            this.individual.localizedDetail.localizedCity = model.localizedCity;
            this.individual.localizedDetail.localizedAddress = model.localizedAddress;

            return Helper.errorsHelper().createErrorResponse<null>(response);
        }

        return Helper.createSuccessResponse<boolean>(true);
    }

    private registerLocalizedDetailMap(
        response: ApiResponse<ApiError[] | IndividualLocalizedDetailInterface>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<IndividualLocalizedDetailInterface>(response);
        }

        const data = response.data as IndividualLocalizedDetailInterface;

        this.individual.localizedDetail = new IndividualLocalizedDetailModel(data);

        return Helper.createSuccessResponse<boolean>(true);
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        this.controls = [];

        if (this.individual && this.individual.localizedDetail) {
            const controls = this.localizedDetailsControlsMap(this.individual.localizedDetail);

            controls.map((control: BaseControl) => {
                this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            this.controls.push(...controls);
        } else {
            this.controls = this.localizedDetailsControlsMap();

            this.controls.map((control: BaseControl) => {
                this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });
        }
    }

    validateForm(
        messageFunction: (controlLabel?: string) => { [key: string]: string; }
    ): boolean {
        if (!this.formGroup.valid) {
            this.controls.map((control: BaseControl) => {
                if (!this.formGroup.controls[control.name]) {
                    return false;
                }

                const formControl = this.formGroup.controls[control.name];

                if (formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((key: string) => {
                        return messageFunction(control.placeholder)[key];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | null>> {
        if (this.formGroup.dirty && this.individual) {
            if (this.individual.localizedDetail) {
                const clonedModel = this.individual.localizedDetail.serialize();

                this.individual.localizedDetail.localizedFirstName =
                    this.formGroup.value[LocalizedDetailFirstNameSelector];

                this.individual.localizedDetail.localizedMiddleName =
                    this.formGroup.value[LocalizedDetailMiddleNameSelector];

                this.individual.localizedDetail.localizedLastName =
                    this.formGroup.value[LocalizedDetailLastNameSelector];

                this.individual.localizedDetail.localizedState =
                    this.formGroup.value[LocalizedDetailStateSelector];

                this.individual.localizedDetail.localizedCity =
                    this.formGroup.value[LocalizedDetailCitySelector];

                this.individual.localizedDetail.localizedAddress =
                    this.formGroup.value[LocalizedDetailAddressSelector];

                return this.localizedDetailsService.update(this.individual.localizedDetail)
                    .pipe(
                        map(this.updateLocalizedDetailMap.bind(this, clonedModel))
                    );
            }

            const inputModel = new IndividualLocalizedDetailInputModel(
                this.formGroup.value[LocalizedDetailFirstNameSelector],
                this.formGroup.value[LocalizedDetailMiddleNameSelector],
                this.formGroup.value[LocalizedDetailLastNameSelector],
                this.formGroup.value[LocalizedDetailStateSelector],
                this.formGroup.value[LocalizedDetailCitySelector],
                this.formGroup.value[LocalizedDetailAddressSelector],
                this.individual.id,
                CulturesEnum.Cyrillic
            );

            return this.localizedDetailsService.register(inputModel)
                .pipe(
                    map(this.registerLocalizedDetailMap.bind(this))
                );
        }

        return null;
    }
}
